<script setup lang="ts">
const { showDialog, creditCount, choose, cancel } = useExportCreditConfirm()
const { t } = useI18n()
const { exportCreditPrice } = usePremiumPlans()

const hasCredits = computed(() => creditCount.value > 0)
const remaining = computed(() => creditCount.value - 1)
const isLast = computed(() => creditCount.value === 1)

// Prevent phantom clicks from propagating from previously closed modals
const backdropReady = ref(false)
let backdropTimer: ReturnType<typeof setTimeout> | null = null

watch(showDialog, (val) => {
  if (val) {
    backdropReady.value = false
    backdropTimer = setTimeout(() => { backdropReady.value = true }, 400)
  } else {
    backdropReady.value = false
    if (backdropTimer) clearTimeout(backdropTimer)
  }
})

function handleBackdropClick() {
  if (backdropReady.value) cancel()
}

function handleKeyDown(event: KeyboardEvent) {
  if (!showDialog.value) return
  if (event.key === 'Escape') cancel()
}

onMounted(() => document.addEventListener('keydown', handleKeyDown))
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
  if (backdropTimer) clearTimeout(backdropTimer)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- Teleported to <body>, so it opens the `.sofax` scope itself -->
      <div
        v-if="showDialog"
        class="sofax fixed inset-0 z-[9999] flex items-center justify-center bg-ink/60 px-4 backdrop-blur-sm"
        @click.self="handleBackdropClick"
      >
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="translate-y-2 scale-[0.98] opacity-0"
          enter-to-class="translate-y-0 scale-100 opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-[0.98] opacity-0"
        >
          <div
            v-if="showDialog"
            class="relative w-full max-w-md rounded-[0.625rem] bg-white p-6 shadow-lift sm:p-8 dark:bg-gray-900 dark:ring-1 dark:ring-gray-700"
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-choice-title"
          >
            <button
              type="button"
              class="social-link absolute right-3 top-3"
              :aria-label="t('common.close')"
              @click="cancel"
            >
              <i class="bi bi-x-lg text-base" aria-hidden="true" />
            </button>

            <div class="mb-6 text-center">
              <div class="mb-4 flex justify-center">
                <SofaxIconTile icon="bi-file-earmark-pdf" size="md" />
              </div>
              <h3 id="export-choice-title" class="mb-2 !text-xl md:!text-2xl">
                {{ t('cv.exportChoice.title') }}
              </h3>
              <p class="!text-sm text-muted dark:text-gray-400">
                {{ hasCredits ? t('cv.exportChoice.subtitleCredits', { n: creditCount }) : t('cv.exportChoice.subtitleFree') }}
              </p>
            </div>

            <div class="space-y-3">
              <!-- Clean PDF: spend a credit, or buy one -->
              <button
                type="button"
                class="group flex w-full items-start gap-4 rounded-[0.625rem] border border-ink bg-white p-4 text-left transition-all duration-300 hover:shadow-lift focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-white dark:bg-gray-800"
                @click="choose(hasCredits ? 'clean' : 'buy')"
              >
                <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-white">
                  <i class="bi bi-stars text-lg" aria-hidden="true" />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="font-title text-base font-semibold text-ink dark:text-white">
                    {{ t('cv.exportChoice.cleanTitle') }}
                  </div>
                  <div class="mt-0.5 text-sm text-body dark:text-gray-300">
                    <template v-if="hasCredits">
                      {{ isLast ? t('premium.upsell.creditConfirm.descriptionLast') : t('premium.upsell.creditConfirm.description', { remaining }) }}
                    </template>
                    <template v-else>
                      {{ t('cv.exportChoice.cleanBuy', { price: exportCreditPrice }) }}
                    </template>
                  </div>
                </div>
                <i class="bi bi-arrow-right mt-2 text-ink transition-transform duration-300 group-hover:translate-x-1 dark:text-white" aria-hidden="true" />
              </button>

              <!-- Free watermarked PDF -->
              <button
                type="button"
                class="group flex w-full items-start gap-4 rounded-[0.625rem] border border-line bg-white p-4 text-left transition-all duration-300 hover:border-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-gray-700 dark:bg-gray-800 dark:hover:border-white/60"
                @click="choose('watermark')"
              >
                <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface text-ink dark:bg-gray-700 dark:text-white">
                  <i class="bi bi-download text-lg" aria-hidden="true" />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="font-title text-base font-semibold text-ink dark:text-white">
                    {{ t('cv.exportChoice.watermarkTitle') }}
                  </div>
                  <div class="mt-0.5 text-sm text-body dark:text-gray-300">
                    {{ t('cv.exportChoice.watermarkDescription') }}
                  </div>
                </div>
                <i class="bi bi-arrow-right mt-2 text-muted transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </button>
            </div>

            <button
              type="button"
              class="mx-auto mt-5 block text-sm font-semibold text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white"
              @click="cancel"
            >
              {{ t('premium.upsell.creditConfirm.cancel') }}
            </button>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
